import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { DatabaseZap, Download, Loader2, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Empty52, useFmt } from "@/components/v52/Bits";
import {
  BackupPanel, CensusPanel, ExecuteDialog, SimPanel, StepsPanel, downloadReport, errText, useImportCall,
} from "@/components/v52/ImportParts";
import { useT52 } from "@/i18n/v52";

function MetaStrip({ st }) {
  const { t52 } = useT52();
  const fmt = useFmt();
  const p = st.package;
  return (
    <div className="f52-imp-meta">
      <div className="card-surface rounded-xl" data-testid="imp-meta-env">
        <small>{t52("imp_env")}</small><strong>{st.is_preview ? t52("imp_env_preview") : t52("imp_env_prod")}</strong>
      </div>
      <div className="card-surface rounded-xl" data-testid="imp-meta-package">
        <small>{t52("imp_pkg")}</small>
        <strong className={p.ok ? "" : "f52-imp-warn"}>{p.ok ? t52("imp_pkg_ok", { n: p.files }) : `${t52("imp_pkg_bad")} · ${p.error}`}</strong>
        {p.ok && <small>{t52("imp_pkg_from", { date: fmt(p.created_at) })} · <span className="f52-mono">{p.manifest_sha256.slice(0, 12)}…</span></small>}
      </div>
      <div className="card-surface rounded-xl" data-testid="imp-meta-target">
        <small>{t52("imp_target")}</small>
        <strong className="f52-mono">{t52("imp_target_db")}: {st.target.control_db}</strong>
        <strong className="f52-mono">{t52("imp_target_app")}: {st.target.canonical_host}</strong>
      </div>
    </div>
  );
}

export default function DataImport() {
  const { t52, tOr } = useT52();
  const [st, setSt] = useState(null);
  const [execStep, setExecStep] = useState(null);
  const [pending, setPending] = useState(false);
  const load = useCallback(() => api.get("/control-import/status").then((r) => setSt(r.data)).catch(() => setSt(false)), []);
  useEffect(() => { load(); }, [load]);
  const activeId = st?.active_job?.id;
  useEffect(() => {
    if (!activeId) return undefined;
    const id = setInterval(load, 2000);
    return () => clearInterval(id);
  }, [activeId, load]);
  const call = useImportCall(load, setPending);

  if (st === null) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-fundio-gold" /></div>;
  const header = (actions) => <PageHeader title={t52("imp_title")} subtitle={t52("imp_lead")}>{actions}</PageHeader>;
  if (st === false) return <div data-testid="data-import-page">{header()}<p data-testid="imp-load-error" className="f52-imp-warn">{t52("imp_load_err")}</p></div>;
  if (!st.enabled) {
    return (
      <div data-testid="data-import-page">
        {header()}
        <Empty52 icon={DatabaseZap} title={t52("imp_off_t")} text={t52("imp_off_d")} testid="imp-disabled" />
      </div>
    );
  }
  const busy = pending || !!st.active_job;
  const report = async () => {
    try { await downloadReport(); toast.success(t52("imp_report_ok")); } catch (e) { toast.error(errText(t52, tOr, e)); }
  };
  const actions = (
    <div className="flex flex-wrap gap-2">
      <button type="button" onClick={load} data-testid="imp-refresh" className="f52-btn f52-btn-secondary"><RefreshCw aria-hidden="true" />{t52("imp_refresh")}</button>
      <button type="button" onClick={report} data-testid="imp-report" className="f52-btn f52-btn-secondary"><Download aria-hidden="true" />{t52("imp_report")}</button>
    </div>
  );
  const post = (url, body) => () => api.post(url, body);
  return (
    <div data-testid="data-import-page" className="f52-imp">
      {header(actions)}
      <MetaStrip st={st} />
      {st.active_job && (
        <p className="f52-imp-note" data-testid="imp-active-job"><Loader2 aria-hidden="true" className="f52-spin" />{t52("imp_running_note")} · {t52(`imp_js_${st.active_job.status}`)}</p>
      )}
      <CensusPanel census={st.census} fresh={st.census_fresh} busy={busy} onRun={() => call(post("/control-import/census"), "imp_started")} />
      <SimPanel sim={st.simulation} canRun={st.census_fresh} busy={busy} onRun={() => call(post("/control-import/simulate"), "imp_started")} />
      <BackupPanel backup={st.backup} busy={busy} onSave={(f) => call(() => api.put("/control-import/backup-proof", f), "imp_saved")} />
      <StepsPanel steps={st.steps} busy={busy} canDry={st.census_fresh} preview={st.is_preview}
        onDry={(k) => call(post(`/control-import/dry-run/${k}`), "imp_started")} onExec={setExecStep} />
      {execStep && (
        <ExecuteDialog step={execStep} preview={st.is_preview} onCancel={() => setExecStep(null)}
          onSubmit={async (b) => { if (await call(post(`/control-import/execute/${execStep.key}`, b), "imp_started")) setExecStep(null); }} />
      )}
    </div>
  );
}
